import { getBlockContent, type StreamingBlock, type StreamingChatId } from "@storytell/streaming";
import {
  TbBellRinging,
  TbBolt,
  TbBrandGithub,
  TbBrandSlack,
  TbCalendarEvent,
  TbChevronRight,
  TbMail,
  TbPlug,
  TbPlugConnected,
  TbTag,
} from "solid-icons/tb";
import { createEffect, createMemo, createSignal, For, type JSX, Match, mapArray, Show, Switch } from "solid-js";
import { blockJsonType, safeParseBlockJson } from "../message/parseBlockContent";
import { type ChatSlots, useChatRuntime } from "../runtime/ChatRuntime";
import { GradientBorder } from "../ui";
import { ConfirmActionRenderer } from "./ConfirmActionRenderer";
import {
  type ConfirmActionPayload,
  isChiefAction,
  isChiefActionsAction,
  isGDriveAction,
  isGitHubAction,
  isGmailAction,
  isLinearAction,
  isMCPAction,
  isNotionAction,
  isSlackAction,
  parseConfirmActionPayload,
} from "./confirmActionSchema";
import { EmailDraftRenderer } from "./EmailDraftRenderer";
import { DriveIcon } from "./gdrive/driveIcon";
import { IntegrationSuggestionRenderer } from "./IntegrationSuggestionRenderer";
import { LinearIcon } from "./linear/linearIcon";
import { NotionIcon } from "./notion/notionIcon";
import { ConnectSlackRenderer } from "./slack/ConnectSlackRenderer";
import { useChatToolActions } from "./useToolActions";

interface ActionRequiredSectionProps {
  blocks: StreamingBlock[];
  chatId: StreamingChatId;
}

type ToolKey =
  | "calendar"
  | "email"
  | "slack"
  | "notion"
  | "drive"
  | "linear"
  | "github"
  | "mcp"
  | "integrations"
  | "actions"
  | "library";

interface ToolSection {
  labelKey: string;
  Icon: (props: { class?: string }) => JSX.Element;
  blocks: StreamingBlock[];
}

const rawBlockContent = (block: StreamingBlock): string | undefined =>
  (block.finalContent?.type === "text" ? block.finalContent.text : undefined) ?? getBlockContent(block.blockId);

// Every blockJsonType call is a full parse of the payload, so callers take the
// type once and only reach here for confirm_action blocks.
const payloadOf = (block: StreamingBlock): ConfirmActionPayload | null => {
  const raw = rawBlockContent(block);
  return raw ? parseConfirmActionPayload(safeParseBlockJson(raw)) : null;
};

const isAlwaysActionable = (type: string | undefined) =>
  type === "email_draft" || type === "connect_slack" || type === "integration_suggestion";

// Unrecognized confirm payloads fall back to Email so they still surface instead of vanishing.
export const toolOf = (block: StreamingBlock): ToolKey => {
  const type = blockJsonType(block);
  if (type === "connect_slack") return "slack";
  if (type === "integration_suggestion") return "integrations";
  const payload = type === "confirm_action" ? payloadOf(block) : null;
  if (!payload) return "email";
  if (isSlackAction(payload)) return "slack";
  if (isNotionAction(payload)) return "notion";
  if (isGDriveAction(payload)) return "drive";
  if (isLinearAction(payload)) return "linear";
  if (isGitHubAction(payload)) return "github";
  if (isMCPAction(payload)) return "mcp";
  if (isChiefActionsAction(payload)) return "actions";
  if (isChiefAction(payload)) return "library";
  if (isGmailAction(payload)) return "email";
  return "calendar";
};

// Surfaces confirm actions (pending or resolved) and email drafts outside the
// collapsible timeline, whose spectator mode leaves Approve/Reject controls
// unclickable.
const ActionRequiredSection = (props: ActionRequiredSectionProps) => {
  const t = useChatRuntime().t;
  const [expanded, setExpanded] = createSignal<boolean | null>(null);
  const chatActions = useChatToolActions(
    () => props.chatId,
    () => true
  );

  // Email drafts, Slack connect prompts and integration suggestions carry no
  // confirm payload and are always actionable, so they keep the "action
  // required" title.
  const hasPending = createMemo(() =>
    props.blocks.some((block) => {
      const type = blockJsonType(block);
      if (type !== "confirm_action") return isAlwaysActionable(type);
      const payload = payloadOf(block);
      if (payload?.status !== "pending") return false;
      const fetched = chatActions.statusOf(payload.actionId);
      return fetched === undefined || fetched === "pending";
    })
  );

  // The default open state is a one-shot snapshot of whether anything was
  // pending when statuses first settled; resolving the last pending action
  // in-session must not auto-collapse the section under the user.
  createEffect(() => {
    if (expanded() === null && chatActions.isSettled()) setExpanded(hasPending());
  });

  const isOpen = () => expanded() ?? hasPending();

  const sections = createMemo<ToolSection[]>(() => {
    const byTool: Record<ToolKey, StreamingBlock[]> = {
      calendar: [],
      email: [],
      slack: [],
      notion: [],
      drive: [],
      linear: [],
      github: [],
      mcp: [],
      integrations: [],
      actions: [],
      library: [],
    };
    for (const block of props.blocks) {
      byTool[toolOf(block)].push(block);
    }
    const out: ToolSection[] = [];
    if (byTool.calendar.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_calendar",
        Icon: (iconProps) => <TbCalendarEvent class={iconProps.class} />,
        blocks: byTool.calendar,
      });
    }
    if (byTool.email.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_email",
        Icon: (iconProps) => <TbMail class={iconProps.class} />,
        blocks: byTool.email,
      });
    }
    if (byTool.slack.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_slack",
        Icon: (iconProps) => <TbBrandSlack class={iconProps.class} />,
        blocks: byTool.slack,
      });
    }
    if (byTool.notion.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_notion",
        Icon: (iconProps) => <NotionIcon class={iconProps.class} />,
        blocks: byTool.notion,
      });
    }
    if (byTool.drive.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_drive",
        Icon: (iconProps) => <DriveIcon class={iconProps.class} />,
        blocks: byTool.drive,
      });
    }
    if (byTool.linear.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_linear",
        Icon: (iconProps) => <LinearIcon class={iconProps.class} />,
        blocks: byTool.linear,
      });
    }
    if (byTool.github.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_github",
        Icon: (iconProps) => <TbBrandGithub class={iconProps.class} />,
        blocks: byTool.github,
      });
    }
    if (byTool.mcp.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_mcp",
        Icon: (iconProps) => <TbPlug class={iconProps.class} />,
        blocks: byTool.mcp,
      });
    }
    if (byTool.integrations.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_integrations",
        Icon: (iconProps) => <TbPlugConnected class={iconProps.class} />,
        blocks: byTool.integrations,
      });
    }
    if (byTool.actions.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_actions",
        Icon: (iconProps) => <TbBolt class={iconProps.class} />,
        blocks: byTool.actions,
      });
    }
    if (byTool.library.length > 0) {
      out.push({
        labelKey: "action_required_section.tool_library",
        Icon: (iconProps) => <TbTag class={iconProps.class} />,
        blocks: byTool.library,
      });
    }
    return out;
  });

  return (
    <GradientBorder rounded="rounded-2xl" expandOnHover>
      <button
        type="button"
        onClick={() => setExpanded(!isOpen())}
        class="flex w-full cursor-pointer items-center gap-2 px-2.5 py-2 text-left text-sm text-primary-text transition-colors hover:bg-inset/50"
      >
        <span class="flex size-4 shrink-0 items-center justify-center">
          <TbBellRinging class="size-3.5 text-brand" />
        </span>
        <span class="min-w-0 shrink truncate text-sm font-medium text-primary-text">
          {hasPending() ? t("action_required_section.title") : t("action_required_section.title_actions")}
        </span>
        <span class="inline-flex min-w-4 shrink-0 items-center justify-center rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] leading-none font-medium text-brand">
          {props.blocks.length}
        </span>
        <TbChevronRight
          class="ms-auto size-3 shrink-0 text-muted-text transition-transform duration-100"
          classList={{ "rotate-90": isOpen() }}
        />
      </button>

      <Show when={isOpen()}>
        <div class="flex flex-col divide-y divide-primary-border border-t border-primary-border">
          <For each={sections()}>
            {(section) => (
              <div class="flex flex-col">
                <div class="flex items-center gap-2 px-2.5 pt-2.5 pb-1.5 text-muted-text">
                  <section.Icon class="size-3.5 shrink-0" />
                  <span class="text-xs font-medium tracking-wide uppercase">{t(section.labelKey)}</span>
                  <span class="text-[10px] tabular-nums">{section.blocks.length}</span>
                </div>
                <div class="flex flex-col divide-y divide-primary-border/30">
                  <For each={section.blocks}>
                    {(block) => {
                      const type = createMemo(() => blockJsonType(block));
                      return (
                        <div class="p-2.5">
                          <Switch>
                            <Match when={type() === "email_draft"}>
                              <EmailDraftRenderer block={block} flat />
                            </Match>
                            <Match when={type() === "confirm_action"}>
                              <ConfirmActionRenderer block={block} chatId={props.chatId} surface="pinned" flat />
                            </Match>
                            <Match when={type() === "connect_slack"}>
                              <ConnectSlackRenderer block={block} flat />
                            </Match>
                            <Match when={type() === "integration_suggestion"}>
                              <IntegrationSuggestionRenderer block={block} flat />
                            </Match>
                          </Switch>
                        </div>
                      );
                    }}
                  </For>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>
    </GradientBorder>
  );
};

/**
 * The `messageActionRequired` slot both apps register: hoists confirm-action,
 * email-draft, connect-slack and integration-suggestion blocks out of the
 * activity timeline (whose spectator mode leaves their controls unclickable)
 * into the pinned section above the answer. Resolved confirm actions stay hoisted too, so an approved
 * card remains surfaced at the top of the message instead of sinking into the
 * timeline.
 */
export const messageActionRequiredSlot: NonNullable<ChatSlots["messageActionRequired"]> = (args) => {
  const rt = useChatRuntime();

  // The turn's block list changes on every operation and block completion, and
  // classifying a block parses its whole payload; one memo per block re-parses
  // only the block that changed instead of every payload in the turn.
  const hoisted = mapArray(
    () => args.activityOps().flatMap(({ operation }) => operation.blocks ?? []),
    (blockId) =>
      createMemo(() => {
        const block = rt.store.block(blockId);
        if (!block) return undefined;
        const type = blockJsonType(block);
        if (isAlwaysActionable(type)) return block;
        return type === "confirm_action" && payloadOf(block) !== null ? block : undefined;
      })
  );

  // Keeps the previous array while the same blocks are hoisted: a fresh one
  // rebuilds every section and remounts the pinned cards under the user.
  const actionBlocks = createMemo(
    () => {
      const blocks: StreamingBlock[] = [];
      for (const entry of hoisted()) {
        const block = entry();
        if (block) blocks.push(block);
      }
      return blocks;
    },
    [],
    { equals: (prev, next) => prev.length === next.length && prev.every((block, i) => block === next[i]) }
  );

  return {
    hoistedBlockIds: () => new Set(actionBlocks().map((b) => b.blockId)),
    section: () => (
      <Show when={actionBlocks().length > 0}>
        <div class="mb-2">
          <ActionRequiredSection blocks={actionBlocks()} chatId={args.chatId as StreamingChatId} />
        </div>
      </Show>
    ),
  };
};
